import { FC, Suspense, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from 'primereact/button';
import { Class } from '../../models/Class';
import { Roles } from '../../models/User';
import { useAppSelector } from '../../redux/hooks';
import { selectUser } from '../../redux/reducers/userSlice';
import { isUserAdmin } from '../../businessLogicService/isUserAdmin';
import { useGetClassesByRole } from '../../custom-hooks/useGetClassesByRole';
import { useChildPageTitle } from '../../custom-hooks/useChildPageTitle';
import { useSideNavActive } from '../LayoutWrapper/SideNavLayoutContext';
import { IconField } from 'primereact/iconfield';
import { InputIcon } from 'primereact/inputicon';
import { InputText } from 'primereact/inputtext';
import { selectSelectedClassIds } from '../../redux/reducers/globalClassFilterSlice';
import { useHiddenClassIds } from '../../custom-hooks/useHiddenClassIds';
import { formatClassDisplayName } from '../../utils/formatClassDisplayName';
import HorizontalLoading from '../../assets/HorizontalLoading/HorizontalLoading';
import ClassesRosterLeftSide from './ClassesRosterLeftSide';
import ClassesRostersRightSide from './ClassesRosterRightSide';
import ClassesRosterCleverInfo from './ClassesRosterCleverInfo';

interface Props {
  classId: string;
  onSelectClass: (classId: string) => void;
  onClassLoaded?: (name: string) => void;
}

const ClassesRosterDetail: FC<Props> = ({ classId, onSelectClass, onClassLoaded }) => {
  const navigate = useNavigate();
  const userRedux = useAppSelector(selectUser);
  const isAdmin = isUserAdmin(userRedux);
  const [refetchSingleClassKey, setRefetchSingleClassKey] = useState(0);
  const [selectedClass, setSelectedClass] = useState<Class | null>(null);
  const [visibleControls, setVisibleControls] = useState(false);
  const hiddenClassIds = useHiddenClassIds();

  useChildPageTitle(selectedClass?.name);
  const sideNavActive = useSideNavActive();
  const [studentSearch, setStudentSearch] = useState('');
  const globalClassIds = useAppSelector(selectSelectedClassIds) ?? [];

  useEffect(() => {
    if (!sideNavActive || isAdmin) return;
    if (globalClassIds.length !== 1) return;
    if (globalClassIds[0] === classId) return;

    onSelectClass(globalClassIds[0]);
  }, [sideNavActive, isAdmin, globalClassIds, classId, onSelectClass]);

  const { allClassesLoaded, allClassesData, refetch } = useGetClassesByRole(false, false, null, true);
  const hasRetried = useRef(false);

  useEffect(() => {
    hasRetried.current = false;
  }, [classId]);

  useEffect(() => {
    if (hiddenClassIds.has(classId)) {
      navigate('/classes-rosters', { replace: true });
      return;
    }
    const found = (allClassesData ?? []).find(cls => cls?.id === classId);
    if (found) {
      const sectionName = formatClassDisplayName(found);
      setSelectedClass({ ...found, sectionName });
      // Breadcrumb shows only the class's name — not the section/owner's last name that sectionName appends.
      onClassLoaded?.(found.name ?? '');
      return;
    }
    if (allClassesLoaded && !hasRetried.current) {
      hasRetried.current = true;
      refetch();
    }
  }, [allClassesData, allClassesLoaded, classId, hiddenClassIds, navigate, onClassLoaded, refetch]);

  const handleSelectClass = (cls: Class) => {
    setSelectedClass(cls);
    onSelectClass(cls.id);
  };

  const currentUserId = `UserNode:${userRedux?.id}`;
  const isCoTeacherOfClass =
    selectedClass?.users?.some(
      user => user?.id === currentUserId && user?.isActive && user?.roles?.some(role => role?.name === Roles.TEACHER),
    ) ?? false;
  const userOwnerOfClass = isAdmin || currentUserId === selectedClass?.owner?.id || isCoTeacherOfClass;

  if (!allClassesLoaded) {
    return <HorizontalLoading />;
  }

  return (
    <>
      {!sideNavActive && (
        <div className="col-10 col-offset-1 lg:col-12 lg:col-offset-0 nc_sidebar flex justify-content-between align-items-center">
          <h1 data-testid="name-section" className="ml-2 flex align-items-center gap-xs">
            <span>{selectedClass?.name ?? ''}</span>
            {selectedClass?.section && <span className="font-normal tw-text-lg">· {selectedClass.section}</span>}
          </h1>
          {selectedClass?.externalSync && <ClassesRosterCleverInfo />}
        </div>
      )}
      {sideNavActive && !isAdmin && (
        <div className="col-12 nc_class-info-column nc_student-search-row">
          <IconField iconPosition="left" className="nc_classes-search w-full">
            <InputIcon className="nc_icon-nc_search icon-sm"> </InputIcon>
            <InputText
              value={studentSearch}
              onChange={event => setStudentSearch(event.target.value)}
              placeholder="Search by student name"
              className="w-full"
              aria-label="Search by student name"
            />
          </IconField>
        </div>
      )}
      <div className={sideNavActive ? 'col-12 nc_class-info-column' : 'col-offset-1 lg:col-offset-0 lg:col-3'}>
        <Button
          icon="nc_icon-nc_menu icon-sm"
          aria-label="Open Sidebar"
          tooltip="Open Sidebar"
          rounded
          outlined
          onClick={() => setVisibleControls(!visibleControls)}
          className="lg:hidden"
        />
        <div className={visibleControls ? '' : ' hidden lg:flex'}>
          <ClassesRosterLeftSide
            selectedClass={selectedClass}
            setSelectedClass={handleSelectClass}
            userOwnerOfClass={userOwnerOfClass}
          />
        </div>
      </div>
      <Suspense
        fallback={
          <div
            className={`flex flex-column nc_sidebar-main ${
              sideNavActive ? 'col-12' : 'col-10 col-offset-1 lg:col-9 lg:col-offset-0'
            }`}
          >
            <HorizontalLoading />
          </div>
        }
      >
        <ClassesRostersRightSide
          refetchSingleClassKey={refetchSingleClassKey}
          setRefetchSingleClassKey={setRefetchSingleClassKey}
          selectedClass={selectedClass}
          userOwnerOfClass={userOwnerOfClass}
          setSelectedClass={handleSelectClass}
          studentSearch={sideNavActive && !isAdmin ? studentSearch : undefined}
        />
      </Suspense>
    </>
  );
};

export default ClassesRosterDetail;
